import { Button, Input, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AddIcon from "../../../../assets/icons/AddIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../../assets/icons/SortIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import FullScreenLoading from "../../../../components/common/FullScreenLoading";
import TopCard from "../../../../components/common/TopCard";
import SuccessModal from "../../../../components/Report/SuccessModal";
import AddNewManualProrateModal from "../../../../components/Settings/Leave/AddNewManualProrateModal";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import {
  useAddLeaveAutoProrateRunMutation,
  useAddLeaveOldUserAutoProrateRunMutation,
  useGetLeaveProrateEmployeeListQuery,
} from "../../../../features/leaveProrate/leaveProrateApi";
import useDebounce from "../../../../hooks/useDebounce";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import {
  GetLeaveProrateEmployeeParams,
  GetLeaveProrateEmployeeResponseType,
} from "../../../../types";
import { countParPageSerialNumber } from "../../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
  skipData,
} from "../../../../utils/utils";
import { leaveSettingsTabList } from "./LeaveType";

enum ProrateType {
  Auto = "Auto Prorate",
  Manual = "Manual Prorate",
}

type GetLeaveProrateParams = Omit<
  GetLeaveProrateEmployeeParams,
  "skip" | "limit"
>;

export default function LeaveProrate() {
  // const navigate = useNavigate();
  const location = useLocation();
  const { open, handleOpen, handleClose } = useModal<
    undefined,
    "CONFIRM" | "OLD_USER_CONFIRM" | "SUCCESS"
  >();
  const [filter, setFilter] = useState<GetLeaveProrateParams>();
  const { keyword, handleInputChange } = useDebounce();
  const { page, limit, handlePagination, handleLimit, resetPagination } =
    useTable();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: leaveProrateEmployeeList,
    isLoading: isLeaveProrateEmployeeListLoading,
    isError,
    error,
  } = useGetLeaveProrateEmployeeListQuery({
    params: {
      limit,
      skip: skipData(page, limit),
      ...filter,
      ...(keyword ? { search: keyword } : {}),
    },
  });

  const [
    addLeaveAutoProrateRun,
    {
      isLoading: leaveAutoProrateRunLoading,
      isError: isAutoProrateError,
      error: autoProrateError,
      isSuccess: isAutoProrateSuccess,
    },
  ] = useAddLeaveAutoProrateRunMutation();

  const [
    addLeaveOldUserAutoProrateRun,
    {
      isLoading: syncOldUserProrateRunLoading,
      isError: isOldUserProrateAutoProrateError,
      error: autoOldUserProrateError,
      isSuccess: isOldUserProrateAutoProrateSuccess,
      // data: autoOldUserProrateData,
    },
  ] = useAddLeaveOldUserAutoProrateRunMutation();

  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isError, error, "Get leave prorate data fail");
  useError(isAutoProrateError, autoProrateError, "Prorate list fetched fail");
  useError(
    isOldUserProrateAutoProrateError,
    autoOldUserProrateError,
    "Old user prorate list fetched fail"
  );

  const handleFilter = <K extends keyof GetLeaveProrateParams>(
    filterType: K,
    value: GetLeaveProrateParams[K]
  ) => {
    setFilter((prev) => ({
      ...prev,
      [filterType]: value,
    }));

    resetPagination();
  };

  const handleRunAutoProrate = () => {
    addLeaveAutoProrateRun({
      is_prorated: "yes",
      prorate_type: "bulk",
    });
  };

  const handleOldUserRunAutoProrate = () => {
    addLeaveOldUserAutoProrateRun({
      is_prorated: "yes",
      prorate_type: "bulk",
    });
  };

  const columns: ColumnsType<
    GetLeaveProrateEmployeeResponseType["data"]["prorate_list"][0]
  > = [
    {
      title: "#SL",
      dataIndex: "index",
      align: "center",
      width: 50,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee ID",
      dataIndex: "employee_info",
      sorter: (a, b) =>
        a?.employee_info?.employee_id?.localeCompare(
          b?.employee_info?.employee_id || ""
        ) ?? 0,
      sortIcon: () => <SortIcon />,
      render: (val) => val?.employee_id ?? "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "employee_info",
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        a?.employee_info?.name?.localeCompare(b?.employee_info?.name),
      render: (val) => {
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10">
              <img
                className="w-full h-full rounded-full"
                src={val?.pro_pic}
                alt=""
              />
            </div>
            <div className="flex-1 flex flex-col gap-[2px] text-sm font-medium text-display-body">
              <h6 className="text-body text-base font-semibold">
                {val?.name ?? "Name: n/a"}
              </h6>
              <p className="text-extra-gray">ID: {val?.employee_id ?? "n/a"}</p>
              <p>{val?.designation_name || "Designation: n/a"}</p>
              <p>
                Email:{" "}
                <a
                  className="!text-accent hover:underline"
                  href={`mailto:${val?.email}`}
                >
                  {val?.email || "n/a"}
                </a>
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "employee_info",
      sorter: (a, b) =>
        a?.employee_info?.department_name?.localeCompare(
          b?.employee_info?.department_name
        ),
      sortIcon: () => <SortIcon />,
      render: (val) => val?.department_name ?? "n/a",
    },
    {
      title: "Joining Date",
      sorter: (a, b) =>
        new Date(a?.employee_info?.join_date)?.getTime() -
        new Date(b?.employee_info?.join_date)?.getTime(),
      sortIcon: () => <SortIcon />,
      dataIndex: "employee_info",
      render: (val) => (val?.join_date ? formatDate(val?.join_date) : "n/a"),
    },
    {
      title: "Prorate Type",
      dataIndex: "prorate_types",
      render: (val) => {
        const autoData =
          val?.filter((v: any) => v?.is_auto_prorated)?.length === val?.length
            ? "Auto"
            : "Manual";
        return (
          <span
            className={`p-2 rounded ${
              autoData === "Manual"
                ? "border border-[#FFE176] text-[#D8A800]"
                : "border border-[#B4C4FF] text-[#4A72FF]"
            }`}
          >
            {ProrateType[autoData]}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "employee_info",
      align: "center",
      render: (val) => {
        return (
          <Link to={`${location.pathname}/view/${val?.business_member_id}`}>
            <Button
              icon={<ViewIcon />}
              size="small"
              className="action-button"
            />
          </Link>
        );
      },
    },
  ];

  useEffect(() => {
    if (keyword) resetPagination();
  }, [keyword, resetPagination]);

  // success toast message
  useEffect(() => {
    if (isAutoProrateSuccess) {
      toast("success", "Prorate list fetched successfully");
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoProrateSuccess]);

  // success toast message
  useEffect(() => {
    if (isOldUserProrateAutoProrateSuccess) {
      toast("success", "Old user prorate list fetched successfully");
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOldUserProrateAutoProrateSuccess]);

  return (
    <SidebarLayout title="Leave Settings" tabList={leaveSettingsTabList}>
      {open?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={open?.open}
          handleClose={handleClose}
        />
      )}
      {open.type === "CONFIRM" && (
        <ConfirmModal
          handleClose={handleClose}
          open={open.open}
          confirmTitle="Run Auto Prorate"
          questionText="Are sure to run auto prorate?"
          handleOk={() => {
            handleRunAutoProrate();
            handleClose();
          }}
          okText="Confirm"
        />
      )}
      {open.type === "OLD_USER_CONFIRM" && (
        <ConfirmModal
          handleClose={handleClose}
          open={open.open}
          confirmTitle="Set Old User Auto Prorate"
          questionText="Are you sure you want to set auto-prorate for users who have not been prorated yet?"
          handleOk={() => {
            handleOldUserRunAutoProrate();
            handleClose();
          }}
          okText="Confirm"
        />
      )}
      {((!isAutoProrateError &&
        !isAutoProrateSuccess &&
        leaveAutoProrateRunLoading) ||
        (!isOldUserProrateAutoProrateError &&
          !isOldUserProrateAutoProrateSuccess &&
          syncOldUserProrateRunLoading)) && <FullScreenLoading />}

      <TopCard leftChildren={<div />}>
        <Button
          className="upload-button"
          size="middle"
          // onClick={handleRunAutoProrate}
          onClick={() => handleOpen({ type: "OLD_USER_CONFIRM" })}
          loading={syncOldUserProrateRunLoading}
        >
          Set Old User Auto Prorate
        </Button>
        <Button
          className="outline-button-primary"
          size="middle"
          // onClick={handleRunAutoProrate}
          onClick={() => handleOpen({ type: "CONFIRM" })}
          loading={leaveAutoProrateRunLoading}
        >
          Run Auto Prorate
        </Button>
        <Button
          className="middle-button"
          type="primary"
          htmlType="submit"
          size="middle"
          onClick={() => {
            handleOpen({
              type: "ADD",
            });
          }}
          icon={<AddIcon />}
        >
          Add New Manual Prorate
        </Button>
      </TopCard>

      <CustomTable
        loading={isLeaveProrateEmployeeListLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={leaveProrateEmployeeList?.data?.total_count}
        dataSource={addKeyInArray(leaveProrateEmployeeList?.data?.prorate_list)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search by name or email"
              onChange={(val) => handleInputChange(val)}
            />

            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("department_id", val)}
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />

            <Select
              allowClear
              showSearch
              onChange={(val) => handleFilter("prorate_type", val)}
              placeholder="Select Prorate"
              filterOption={selectSearchOption}
              options={selectStringOption(prorateList, "string", true)}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
          </>
        }
      />

      {open.type === "ADD" && (
        <AddNewManualProrateModal
          open={open.open}
          type={open.type}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
}

const prorateList = ["manual", "auto"];
